import { useEffect } from 'react';

import html2canvas from 'html2canvas';

type CaptureOptions = {
  containerRef: React.RefObject<HTMLElement>;
  getFileName: () => string;
  onBeforeCapture?: () => Promise<void> | void;
  shouldCapture?: () => boolean;
  onAfterDownload?: () => void;
};

export const useIntroAutoCapture = ({
  containerRef,
  getFileName,
  onBeforeCapture,
  shouldCapture,
  onAfterDownload,
}: CaptureOptions) => {
  useEffect(() => {
    const run = async () => {
      if (typeof window === 'undefined') return;
      const params = new URLSearchParams(window.location.search);
      if (params.get('download') !== '1') return;
      if (shouldCapture && !shouldCapture()) return;

      const target = (containerRef.current as HTMLElement) || document.body;
      if (!target) return;

      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

        if (onBeforeCapture) {
          await onBeforeCapture();
        }

        await Promise.race([
          new Promise<void>((resolve) => setTimeout(resolve, 350)),
          (document as unknown as Document & { fonts?: { ready?: Promise<void> } }).fonts?.ready ??
            Promise.resolve(),
        ]);

        const images = Array.from(target.querySelectorAll('img')) as HTMLImageElement[];
        await Promise.all(
          images.map((img) =>
            img.complete
              ? Promise.resolve()
              : new Promise<void>((res) =>
                  img.addEventListener('load', () => res(), { once: true }),
                ),
          ),
        );

        const canvas = await html2canvas(target as HTMLElement, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          logging: false,
          letterRendering: true,
          ignoreElements: (el) =>
            (el as HTMLElement)?.getAttribute?.('data-html2canvas-ignore') !== null,
          onclone: (doc) => {
            const badges = Array.from(
              doc.querySelectorAll('[data-capture-step-number]') ?? [],
            ) as HTMLElement[];
            badges.forEach((_el) => {
              _el.style.transform = 'translateY(13px)';
            });
          },
        });

        const outputCanvas = document.createElement('canvas');
        outputCanvas.width = 1190;
        outputCanvas.height = 1440;
        const ctx = outputCanvas.getContext('2d');
        if (!ctx) return;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, outputCanvas.width, outputCanvas.height);
        ctx.drawImage(canvas, 0, 0, outputCanvas.width, outputCanvas.height);

        const a = document.createElement('a');
        a.href = outputCanvas.toDataURL('image/png');
        a.download = `${getFileName()}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();

        setTimeout(() => {
          try {
            window.close();
          } catch {}
        }, 200);

        if (onAfterDownload) onAfterDownload();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('intro auto capture failed:', e);
      }
    };

    run();
  }, [containerRef, getFileName, onBeforeCapture, onAfterDownload, shouldCapture]);
};

export default useIntroAutoCapture;
